import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft, Play, Send, RotateCcw, Loader2, CheckCircle2,
  XCircle, Sparkles, Terminal, BookOpen, ArrowRight, X
} from "lucide-react";
import Editor from "@monaco-editor/react";
import { ensurePyodide, runCode, runTests } from "../lib/pyodideRunner";
import { loginWithGoogle } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function VerdictBanner({ result }) {
  if (!result) return null;
  const passed = result.verdict === "PASS";
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-dm-sans font-bold ${
        passed
          ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
          : "bg-red-500/15 border-red-500/40 text-red-300"
      }`}
      data-testid="try-verdict-banner"
    >
      {passed ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
      {passed
        ? `All ${result.totalAll} tests passed!`
        : `${result.passedAll}/${result.totalAll} tests passed`}
    </motion.div>
  );
}

function SoftSignupModal({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          data-testid="try-signup-modal"
        >
          <div
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            onClick={onClose}
            aria-label="Close"
          />
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            className="relative max-w-md w-full bg-gradient-to-br from-zinc-900 to-zinc-950 border border-[#00F0FF]/40 rounded-3xl p-8 sm:p-9 shadow-[0_0_60px_rgba(0,240,255,0.25)]"
          >
            <button
              onClick={onClose}
              data-testid="try-signup-close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={14} />
            </button>

            <motion.div
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.1 }}
              className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#7000FF] flex items-center justify-center"
            >
              <CheckCircle2 size={28} className="text-black" />
            </motion.div>

            <div className="text-center">
              <div className="inline-flex items-center gap-1.5 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-full px-3 py-1 text-[10px] font-mono font-bold text-[#00F0FF] uppercase tracking-wider mb-4">
                <Sparkles size={10} /> Lesson 1 complete
              </div>
              <h2 className="font-outfit font-black text-2xl text-white leading-tight mb-2">
                Nice. You wrote real Python<br/>in your browser.
              </h2>
              <p className="text-sm text-zinc-400 font-dm-sans mb-6 leading-relaxed">
                599 lessons to go. Sign up free to save this milestone, unlock the
                AI Tutor, and earn XP as you go.
              </p>
              <button
                onClick={() => loginWithGoogle("/ai-tools/python-lab")}
                data-testid="try-signup-cta"
                className="w-full bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black font-outfit font-black py-3.5 rounded-xl hover:brightness-110 transition-all flex items-center justify-center gap-2"
              >
                Save my progress (free) <ArrowRight size={16} />
              </button>
              <button
                onClick={onClose}
                data-testid="try-signup-skip"
                className="mt-3 text-xs text-zinc-500 hover:text-zinc-300 font-dm-sans transition-colors"
              >
                Keep exploring without signing up
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function TryLessonPage() {
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [code, setCode] = useState("");
  const [pyodideStatus, setPyodideStatus] = useState("loading");
  const [output, setOutput] = useState({ stdout: "", stderr: "", error: null });
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [hasPassed, setHasPassed] = useState(false);
  const editorRef = useRef(null);
  const outputRef = useRef(null);

  // Load lesson from public endpoint
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/python-lab/try-lesson`)
      .then((r) => {
        if (cancelled) return;
        setLesson(r.data);
        setCode(r.data.starter_code || "");
      })
      .catch((e) => {
        console.error("Try lesson fetch failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Boot Pyodide
  useEffect(() => {
    let cancelled = false;
    ensurePyodide()
      .then(() => {
        if (!cancelled) setPyodideStatus("ready");
      })
      .catch((e) => {
        if (!cancelled) {
          console.error("Pyodide load failed", e);
          setPyodideStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollOutputIntoView = () => {
    setTimeout(() => {
      outputRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 60);
  };

  const handleRun = useCallback(async () => {
    if (pyodideStatus !== "ready") return;
    setRunning(true);
    setOutput({ stdout: "", stderr: "", error: null });
    setTestResult(null);
    try {
      const r = await runCode(code);
      setOutput(r);
      scrollOutputIntoView();
    } finally {
      setRunning(false);
    }
  }, [code, pyodideStatus]);

  const handleSubmit = useCallback(async () => {
    if (!lesson || pyodideStatus !== "ready") return;
    setSubmitting(true);
    setTestResult(null);
    try {
      const result = await runTests({
        code,
        visibleTests: lesson.visible_tests || "",
        hiddenTests: lesson.hidden_tests || "",
      });
      setTestResult(result);
      setOutput((o) => ({ ...o, stdout: result.stdout || o.stdout }));
      scrollOutputIntoView();

      if (result.verdict === "PASS" && !hasPassed) {
        setHasPassed(true);
        // Wait a tick so the user sees the green verdict, then nudge for signup
        setTimeout(() => setSignupOpen(true), 900);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  }, [code, lesson, pyodideStatus, hasPassed]);

  const handleReset = () => {
    if (lesson?.starter_code) setCode(lesson.starter_code);
    setOutput({ stdout: "", stderr: "", error: null });
    setTestResult(null);
  };

  if (!lesson) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={28} className="text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-zinc-900 sticky top-0 z-30 bg-[#050505]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="try-back-btn"
            onClick={() => navigate("/ai-tools/python-lab")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-dm-sans transition-colors"
          >
            <ArrowLeft size={16} /> Python Lab
          </button>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-full px-3 py-1 text-[10px] font-mono font-bold text-[#00F0FF] uppercase tracking-wider">
              <Sparkles size={10} /> Free demo · No signup
            </span>
            <button
              onClick={() => loginWithGoogle("/ai-tools/python-lab")}
              data-testid="try-header-signup"
              className="text-xs font-dm-sans font-bold bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white px-3 py-1.5 rounded-lg transition-colors"
            >
              Sign up free
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: brief + body */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 text-[10px] font-mono font-bold text-[#00F0FF] tracking-wider">
            <BookOpen size={12} /> MODULE 01 · LESSON 01
          </div>
          <h1 className="font-outfit font-black text-3xl sm:text-4xl text-white leading-tight">
            {lesson.title}
          </h1>
          <div
            className="prose prose-invert prose-zinc max-w-none text-sm font-dm-sans text-zinc-300 leading-relaxed bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 whitespace-pre-wrap"
            data-testid="try-lesson-body"
          >
            {lesson.body_md}
          </div>

          {/* Why this works strip */}
          <div className="bg-gradient-to-br from-[#00F0FF]/5 to-[#7000FF]/5 border border-zinc-800 rounded-xl p-4">
            <div className="text-[10px] font-mono font-bold text-zinc-400 mb-2 tracking-wider">
              ✨ WHAT MAKES THIS DEMO REAL
            </div>
            <ul className="text-xs text-zinc-300 font-dm-sans space-y-1.5">
              <li>• Python runs in your browser via Pyodide — no install, no server roundtrip</li>
              <li>• Tests grade your code automatically (same grader as the real lessons)</li>
              <li>• 599 more lessons + AI Tutor + certificate after you sign up</li>
            </ul>
          </div>
        </section>

        {/* Right: editor + output */}
        <section className="space-y-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-900/40">
              <div className="flex items-center gap-2 text-xs font-mono text-zinc-400">
                <Terminal size={13} /> main.py
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleReset}
                  data-testid="try-reset-btn"
                  className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1 transition-colors"
                >
                  <RotateCcw size={12} /> Reset
                </button>
              </div>
            </div>
            <div className="h-[320px]">
              <Editor
                height="320px"
                defaultLanguage="python"
                theme="vs-dark"
                value={code}
                onChange={(v) => setCode(v ?? "")}
                onMount={(ed) => { editorRef.current = ed; }}
                options={{
                  fontSize: 14,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  padding: { top: 12, bottom: 12 },
                }}
              />
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-zinc-800 bg-zinc-900/40">
              <div className="text-[11px] font-mono text-zinc-500">
                {pyodideStatus === "loading" && (<><Loader2 size={11} className="inline animate-spin" /> Loading Python runtime…</>)}
                {pyodideStatus === "ready" && "Python runtime ready"}
                {pyodideStatus === "error" && "Runtime failed to load"}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRun}
                  disabled={pyodideStatus !== "ready" || running || submitting}
                  data-testid="try-run-btn"
                  className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-dm-sans font-bold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1.5 transition-colors"
                >
                  {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                  Run
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={pyodideStatus !== "ready" || running || submitting}
                  data-testid="try-submit-btn"
                  className="bg-gradient-to-r from-[#00F0FF] to-[#7000FF] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed text-black font-outfit font-black px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1.5 transition-all"
                >
                  {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  Submit
                </button>
              </div>
            </div>
          </div>

          {/* Output */}
          <div ref={outputRef} className="bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-2 border-b border-zinc-800 bg-zinc-900/40 flex items-center justify-between">
              <span className="text-xs font-mono text-zinc-400">Output</span>
              {testResult && <VerdictBanner result={testResult} />}
            </div>
            <div className="p-4 font-mono text-xs text-zinc-200 min-h-[120px] max-h-[260px] overflow-auto whitespace-pre-wrap">
              {output.error && (
                <div className="bg-red-500/15 border border-red-500/40 text-red-300 rounded-lg px-3 py-2 mb-2" data-testid="try-error-banner">
                  {output.error}
                </div>
              )}
              {output.stderr && <div className="text-red-300">{output.stderr}</div>}
              {output.stdout && <div className="text-zinc-200">{output.stdout}</div>}
              {!output.stdout && !output.stderr && !output.error && (
                <div className="text-zinc-600 italic">
                  Click <strong className="text-zinc-400">Run</strong> to execute, or <strong className="text-zinc-400">Submit</strong> to grade your code.
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <SoftSignupModal open={signupOpen} onClose={() => setSignupOpen(false)} />
    </div>
  );
}
